/// <amd-module name="Comments/Thread/Components/CommentThread" />
import * as React from "react";

import { ObservableArray } from "VSS/Core/Observable";
import { announce } from "VSS/Core/Util/Accessibility";
import { PersonaDetailsProvider } from "VSS/Features/Identities/PersonaDetailsProvider";
import { ILegacyPlatformService } from "VSS/Legacy/Types";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { onClickFPS } from "VSS/Platform/FPS";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IListItemDetails, IListRow, ListItem, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { IPersonaProvider, Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { CommentExpandOptions, CommentReactionType, IComment, ICommentPage, ICommentReaction } from "Comments/Common/Contracts";
import { UsageTelemetryHelper } from "Comments/Common/Telemetry";
import { CommentItem } from "Comments/Item/CommentItem";
import { ICommentReactionTypeInfo, IReactionTypeToInfoMap } from "Comments/Reactions/Contracts";
import { ICommentThreadProps } from "Comments/Thread/Components/CommentThread.Props";
import * as Resources from "Comments/Thread/Resources";
import { ItemType, IUIComment, IUICommentAdd, IUICommentListItem, IUICommentLoadMore } from "./CommentThreadInternalTypes";
import { LoadMoreComponent } from "./LoadMoreComponent";
import { MarkdownCommentPreview } from "./MarkdownCommentPreview";
import { ReplyOrAddComponent } from "./ReplyOrAddComponent";

interface IUICommentPage {
    /** The total number of comments in the page (some may not be paged yet) */
    totalCount: number;

    /** The number of comments that have been paged */
    pagedCount: number;

    /** The continuation token to use to page more data */
    continuationToken: string;

    /** The comment id of the parent comment, if any (undefined if it's a root comment) */
    parentId?: number;
}

/**
 * Type used to map a parent id to a page
 */
type IUICommentChildren = { [parentId: number]: IUICommentPage };

/**
 * State for the comment thread
 */
interface ICommentThreadState {
    /** reference to the root page of the comment thread */
    commentPage?: IUICommentPage;
    /** map from commentid -> IUICommentPage where commentId is the id of a parent (root) commment */
    parentChildMap: IUICommentChildren;
    /** whether or not the component is loading */
    isLoading?: boolean;
    /** the set of comments to be displayed */
    comments: ObservableArray<IUICommentListItem>;
    /** The last artifact this component was showing results for */
    lastArtifact?: string;
    /** the last global error which occurred */
    lastError?: string;
}

/**
 * Composite component for a comment thread
 */
export class CommentThread extends VssComponent<ICommentThreadProps, ICommentThreadState> {
    private _usageTelemetry: UsageTelemetryHelper;
    private _personaProvider: IPersonaProvider;
    private _list = React.createRef<ScrollableList<IUICommentListItem>>();
    private _mentionProcessModulePromise: Promise<any[]>;
    private _currentUser: IdentityRef | undefined;
    private _isDirtyCount: number = 0;

    constructor(props: ICommentThreadProps, context: IVssComponentContext) {
        super(props, context);

        this._usageTelemetry = new UsageTelemetryHelper(props.artifactInfo, context.pageContext);
        this._personaProvider = new PersonaDetailsProvider(context.pageContext);
        this.state = { parentChildMap: {}, comments: new ObservableArray<IUICommentListItem>() };

        this._currentUser = this._getCurrentUser();

        const legacyPlatformService: ILegacyPlatformService = context.pageContext.getService<ILegacyPlatformService>(
            "ILegacyPlatformService"
        );
        this._mentionProcessModulePromise = legacyPlatformService.requireModules([
            "WorkItemTracking/Scripts/Utils/RichTextPreRenderUtility"
        ]);
    }

    public componentWillMount(): void {
        this._initialLoad();
    }

    public componentDidUpdate(prevProps: ICommentThreadProps, prevState: ICommentThreadState): void {
        super.componentDidUpdate(prevProps, prevState);
        if (this.props.artifactInfo.artifactId !== prevProps.artifactInfo.artifactId) {
            this._initialLoad();
        }
    }

    public render(): JSX.Element {
        const { className } = this.props;
        const { commentPage, comments, isLoading, lastError } = this.state;

        if (isLoading) {
            return (
                <div className={css("comment-thread flex-column", className)}>
                    <Spinner className="comment-thread-spinner" size={SpinnerSize.large} label={Resources.Loading} />
                </div>
            );
        }

        if (!commentPage || !comments) {
            return <></>;
        }

        return (
            <div className={className}>
                {this._renderGlobalError(lastError)}
                <ScrollableList<IUICommentListItem>
                    itemProvider={comments}
                    virtualize={true}
                    pageSize={20}
                    rowHeight={30}
                    renderRow={this._renderRow}
                    className="comment-thread"
                    onFocus={this._onFocusItem}
                    ref={this._list}
                    excludeTabStop={true}
                />
            </div>
        );
    }

    private _renderRow = (
        rowIndex: number,
        commentItem: IUICommentListItem,
        details: IListItemDetails<IUICommentListItem>
    ): JSX.Element => {
        switch (commentItem.type) {
            case ItemType.Comment:
                return this._renderComment(commentItem as IUIComment, rowIndex, details);
            case ItemType.Add:
                return this._renderAddReply(commentItem as IUICommentAdd, rowIndex, details);
            case ItemType.LoadMore:
                return this._renderLoadMore(commentItem as IUICommentLoadMore, rowIndex, details);
            default:
                throw new Error("Invalid comment item type");
        }
    };

    private _renderComment(uiComment: IUIComment, rowIndex: number, details: IListItemDetails<IUICommentListItem>): JSX.Element {
        const { artifactInfo, readonly, commentsClient, supportsImageUpload, supportsReactions, formatDisabled } = this.props;

        const id: number = uiComment.comment.id;
        const isChild: boolean = !!uiComment.comment.parentId;
        const commentReactionInfoMap: IReactionTypeToInfoMap = this._getCommentReactions(id);

        return (
            <ListItem className="comment-item" details={details} index={rowIndex} key={id}>
                <div className={css("flex-column flex-grow", isChild && "child-thread")}>
                    {this._renderError(uiComment, true)}
                    <CommentItem
                        key={id}
                        artifactInfo={artifactInfo}
                        comment={uiComment.comment}
                        isEditing={uiComment.isEditing === true}
                        canEdit={
                            !uiComment.readonly && !uiComment.comment.isDeleted && this._isCurrentUserCommentCreator(uiComment.comment)
                        }
                        formatDisabled={formatDisabled}
                        onStartEdit={this._onStartEdit}
                        onCancelEdit={this._onCancelEdit}
                        onUpdate={this._onUpdate}
                        onRenderPreview={this._onRenderPreview}
                        onEditorMounted={this._onEditEditorMounted}
                        readonly={uiComment.readonly || readonly || uiComment.comment.isDeleted}
                        onConfirmDelete={this._onDelete}
                        className="flex-grow"
                        viewerContentClassName="rendered-mark-down"
                        isReactionsEnabled={supportsReactions}
                        onAddReaction={this._onAddReaction}
                        onDeleteReaction={this._onDeleteReaction}
                        onGetRecentlyReactedUsers={this._onGetEngagedUsers}
                        reactionTypeToInfoMap={commentReactionInfoMap}
                        onRenderPersona={this._onRenderPersona}
                        onPostRenderContent={this._onPostRenderContent}
                        onDirtyChange={this._onCommentDirtyChange}
                        uploadImageHandler={
                            supportsImageUpload && commentsClient.addCommentAttachment ? this._uploadImageHandler : undefined
                        }
                    />
                </div>
            </ListItem>
        );
    }

    private _uploadImageHandler = async (file: File): Promise<string> => {
        const { artifactInfo, commentsClient } = this.props;
        const attachment = await commentsClient.addCommentAttachment!(artifactInfo, file);
        return attachment.url;
    };

    private _renderAddReply(addRow: IUICommentAdd, rowIndex: number, details: IListItemDetails<IUICommentListItem>): JSX.Element {
        const { artifactInfo, readonly, commentsClient, supportsImageUpload } = this.props;

        const isChild: boolean = !!addRow.parentId;

        return (
            <ListItem className="comment-item" details={details} index={rowIndex} key={rowIndex}>
                <div className={css("flex-column flex-grow", isChild && "child-thread")}>
                    {this._renderError(addRow, true)}
                    <ReplyOrAddComponent
                        artifactInfo={artifactInfo}
                        commentsClient={commentsClient}
                        readonly={readonly}
                        item={addRow}
                        add={this._onAddComment}
                        onEditorMounted={this._onAddEditorMounted}
                        onRenderPreview={this._onRenderPreview}
                        handleError={this._handleAddError}
                        onCancelAdd={this._onCancelAdd}
                        onDirtyChange={this._updateDirtyCount}
                        uploadImageHandler={
                            supportsImageUpload && commentsClient.addCommentAttachment ? this._uploadImageHandler : undefined
                        }
                    />
                </div>
            </ListItem>
        );
    }

    private _renderLoadMore(loadMoreRow: IUICommentLoadMore, rowIndex: number, details: IListItemDetails<IUICommentListItem>): JSX.Element {
        const { artifactInfo, supportsThreads, commentsClient } = this.props;
        const { parentChildMap, commentPage } = this.state;

        const parentId: number | undefined = loadMoreRow.parentId;
        const isChild: boolean = !!loadMoreRow.parentId;
        const resolvedPage: IUICommentPage | undefined = parentId ? parentChildMap[parentId] : commentPage;

        return (
            <ListItem className="comment-item" details={details} index={rowIndex} key={rowIndex}>
                <div className={css("flex-column flex-grow", isChild && "child-thread")}>
                    {this._renderError(loadMoreRow, isChild)}
                    <LoadMoreComponent
                        artifactInfo={artifactInfo}
                        commentsClient={commentsClient}
                        count={resolvedPage!.pagedCount}
                        totalCount={resolvedPage!.totalCount}
                        continuationToken={resolvedPage!.continuationToken}
                        supportsThreads={supportsThreads}
                        item={loadMoreRow}
                        updatePage={this._onLoadMore}
                        handleError={this._handleLoadMoreError}
                    />
                </div>
            </ListItem>
        );
    }

    private _onPostRenderContent = (content: string, contentDiv: HTMLDivElement): void => {
        if (content && contentDiv) {
            this._mentionProcessModulePromise.then(modules => {
                modules[0].processHtmlWithMentions(content, contentDiv, { onWorkItemClick: this._onWorkItemClick });
            });
        }
    };

    private _onWorkItemClick = (workItem: any, url: string, defaultCallback: () => void): void => {
        onClickFPS(this.context.pageContext, url, true);
    };

    private _renderError = (item: IUICommentListItem, indented: boolean): JSX.Element | undefined => {
        if (!item.lastError) {
            return;
        }
        return this._renderErrorInternal(indented, () => this._clearError(item), item.lastError);
    };

    private _renderGlobalError = (errorText?: string): JSX.Element | undefined => {
        if (!errorText) {
            return;
        }
        return this._renderErrorInternal(false, () => this.setState({ lastError: undefined }), errorText);
    };

    private _renderErrorInternal(indented: boolean, onDismiss: () => void, errorText?: string): JSX.Element {
        return (
            <MessageCard
                severity={MessageCardSeverity.Error}
                className={css("error-card", indented && "indented-error-card")}
                onDismiss={onDismiss}
            >
                {errorText}
            </MessageCard>
        );
    }

    private _onAddEditorMounted = (item: IUICommentAdd): void => {
        const rowIndex: number = this._getPageEndIndex(item.parentId);
        this._scrollIndexIntoView(rowIndex);
    };

    private _onEditEditorMounted = (commentId: number): void => {
        const rowIndex: number = this._getCommentIndexById(commentId);
        this._scrollIndexIntoView(rowIndex);
    };

    private _scrollIndexIntoView(rowIndex: number) {
        if (this._list && this._list.current && rowIndex >= 0) {
            this._list.current.scrollIntoView(rowIndex, { block: "nearest" });
        }
    }

    private _onRenderPersona = (identityRef: IdentityRef): JSX.Element => {
        return (
            <Persona className="comment-avatar" identity={identityRef} size={PersonaSize.size32} personaProvider={this._personaProvider} />
        );
    };

    private _onRenderPreview = (markdown: string): JSX.Element => {
        return (
            <MarkdownCommentPreview
                markdown={markdown}
                artifactInfo={this.props.artifactInfo}
                handleError={e => this._handleError("_onRenderPreview", e)}
            />
        );
    };

    private _onFocusItem = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<IUICommentListItem>): void => {
        const commentItem: IUICommentListItem = listRow.data;

        // for the buttons/links, select the first element that can take focus
        if (commentItem.type === ItemType.Add || commentItem.type === ItemType.LoadMore) {
            const { comments } = this.state;
            let selector: string = "button,[tabIndex]";

            if (listRow.index === comments.length - 1) {
                // last item is the 'add' row, focus on the button instead of
                // the avatar
                selector = "button";
            }

            const target: HTMLElement = event.target as HTMLElement;
            const focusChildren = target.querySelectorAll(selector);

            if (focusChildren.length > 0) {
                (focusChildren[0] as HTMLElement).focus();
            }
        }
    };

    private _onLoadMore = (page: ICommentPage, item: IUICommentLoadMore): void => {
        const { commentPage, parentChildMap } = this.state;

        let pageToUpdate: IUICommentPage | undefined;
        const parentId: number | undefined = item.parentId;

        if (parentId) {
            pageToUpdate = parentChildMap[parentId];
        } else {
            pageToUpdate = commentPage;
        }

        if (pageToUpdate) {
            this._updatePage(page, pageToUpdate);
            this._usageTelemetry.publishPageMoreComments(pageToUpdate.pagedCount, pageToUpdate.totalCount);
        }
    };

    private _onAddComment = (comment: IComment, addItem: IUICommentAdd): void => {
        const { supportsThreads } = this.props;
        const { commentPage, parentChildMap, comments } = this.state;

        const parentId: number | undefined = addItem.parentId;
        const pageToUpdate: IUICommentPage | undefined = parentId ? parentChildMap[parentId] : commentPage;

        if (pageToUpdate) {
            pageToUpdate.totalCount++;
            pageToUpdate.pagedCount++;

            let newCommentIndex: number = this._getPageEndIndex(parentId);

            if (newCommentIndex >= 0) {
                const newComment: IUIComment = { type: ItemType.Comment, comment };
                comments.splice(newCommentIndex, 0, newComment);

                if (!parentId && supportsThreads) {
                    // adding a new root item, add the 'reply' link
                    const addCommentItem: IUICommentAdd = { type: ItemType.Add, parentId: comment.id };
                    newCommentIndex++;
                    comments.splice(newCommentIndex, 0, addCommentItem);

                    // add the new root to the parent child map
                    parentChildMap[comment.id] = this._createEmptyUICommentPage(comment.id);
                }

                if (!parentId && this._list && this._list.current) {
                    // adding to the end of the list, scroll to the bottom to ensure its visible.
                    this._list.current.scrollIntoView(comments.length - 1);
                }
            }

            this._usageTelemetry.publishAddComment(comment.text.length, pageToUpdate.totalCount);
            this._clearError(addItem);
        }
    };

    private _onCancelAdd = (item: IUICommentAdd): void => {
        this._clearError(item);
    };

    private _onDelete = async (commentId: number, confirmed: boolean): Promise<void> => {
        if (confirmed) {
            const { artifactInfo, commentsClient, supportsThreads } = this.props;
            const { parentChildMap, comments } = this.state;

            const page: IUICommentPage | undefined = this._getCommentPageFromCommentId(commentId);

            if (!page) {
                return;
            }

            try {
                this._setReadOnlyState(commentId, true);

                const commentIndex: number = this._getCommentIndexById(commentId);
                const comment: IUIComment = comments.value[commentIndex] as IUIComment;

                if (comment.comment.isDeleted) {
                    return;
                }

                await commentsClient.deleteComment(artifactInfo, commentId);
                announce(Resources.CommentDeletedMessage);

                page.totalCount--;
                page.pagedCount--;

                this._usageTelemetry.publishDeleteComment(confirmed, page.totalCount);
                if (!supportsThreads) {
                    comments.splice(commentIndex, 1);
                } else {
                    if (!page.parentId) {
                        // item is a 'root'.  Mark it as deleted.
                        // if it has children, continue to render them and the reply/load more experiences
                        // if not, then remove the reply/loadmore items

                        const childPage: IUICommentPage = parentChildMap[commentId];

                        if (childPage.totalCount > 0) {
                            comment.comment = { ...comment.comment, isDeleted: true, renderedText: Resources.Deleted };
                            comments.change(commentIndex, comment);
                        } else {
                            // has no more children, remove the comment and remaining children.
                            const endIndex: number = this._getPageEndIndex(commentId);

                            comments.splice(commentIndex, endIndex - commentIndex + 1);
                        }
                    } else {
                        // not parent page, just remove it.
                        comments.splice(commentIndex, 1);
                    }
                }
                this._clearError(comment);
            } catch (e) {
                this._handleError("_onDelete", e, comments.value[this._getCommentIndexById(commentId)]);
            } finally {
                this._setReadOnlyState(commentId, false);
            }
        }
    };

    private _onUpdate = async (commentId: number, newValue: string, oldValue: string): Promise<void> => {
        const { artifactInfo, commentsClient } = this.props;
        const { comments } = this.state;
        const page: IUICommentPage | undefined = this._getCommentPageFromCommentId(commentId);

        if (!page) {
            return;
        }

        try {
            const updatedComment: IComment = await commentsClient.updateComment(artifactInfo, commentId, newValue);
            announce(Resources.CommentUpdatedMessage);

            const comment: IUIComment = { type: ItemType.Comment, comment: updatedComment };
            const commentIndex: number = this._getCommentIndexById(commentId);

            if (commentIndex >= 0) {
                const { comments } = this.state;

                comments.change(commentIndex, comment);
            }
            this._setEditState(commentId, false);
        } catch (e) {
            this._handleError("_onUpdate", e, comments.value[this._getCommentIndexById(commentId)]);
        }
    };

    private _onStartEdit = (commentId: number): void => {
        this._setEditState(commentId, true);
    };

    private _onCancelEdit = (commentId: number, isDirty: boolean): void => {
        const { comments } = this.state;

        this._setEditState(commentId, false);
        this._clearError(comments.value[this._getCommentIndexById(commentId)]);
    };

    private _setEditState(commentId: number, isEditing: boolean): void {
        this._updateCommentState(commentId, (comment: IUIComment) => {
            if (isEditing !== !!comment.isEditing) {
                comment.isEditing = isEditing;
                return true;
            }
            return false;
        });
    }

    private _setReadOnlyState(commentId: number, readonly: boolean): void {
        this._updateCommentState(commentId, (comment: IUIComment) => {
            if (readonly !== !!comment.readonly) {
                comment.readonly = readonly;
                return true;
            }
            return false;
        });
    }

    private _updateCommentState(commentId: number, callbackfn: (comment: IUIComment) => boolean): void {
        const commentIndex: number = this._getCommentIndexById(commentId);

        if (commentIndex >= 0) {
            const { comments } = this.state;

            const comment: IUIComment = comments.value[commentIndex] as IUIComment;

            if (callbackfn(comment)) {
                comments.change(commentIndex, comment);
            }
        }
    }

    private _onCommentDirtyChange = (commentId: number, isDirty: boolean): void => {
        this._updateDirtyCount(isDirty);
    };

    private _handleAddError = (item: IUICommentAdd, error: any): void => {
        this._handleError("addComment", error, item);
    };

    private _handleLoadMoreError = (item: IUICommentLoadMore, error: any): void => {
        this._handleError("loadMore", error, item);
    };

    private _clearError = (item: IUICommentListItem): void => {
        this._updateItemError(item, undefined);
    };

    private _updateItemError(item: IUICommentListItem, errorText?: string): void {
        const { comments } = this.state;

        if (item) {
            if (item.lastError !== errorText) {
                item.lastError = errorText;

                const commentIndex: number = this._getCommentIndex(item);

                if (commentIndex >= 0) {
                    comments.change(commentIndex, item);
                }
            }
        }
    }

    private _getCommentPageFromCommentId(commentId: number): IUICommentPage | undefined {
        const { commentPage, parentChildMap, comments } = this.state;

        if (!commentPage) {
            return undefined;
        }

        const commentIndex: number = this._getCommentIndexById(commentId);

        if (commentIndex >= 0) {
            const parentId: number | undefined = (comments.value[commentIndex] as IUIComment).comment.parentId;
            return parentId ? parentChildMap[parentId] : commentPage;
        }

        return undefined;
    }

    /**
     * Loads the initial set of comments from the provider
     */
    private async _initialLoad(): Promise<void> {
        let { commentPage, lastArtifact } = this.state;
        const { commentsClient, artifactInfo, supportsThreads } = this.props;
        const includeChildrenFlag: number = supportsThreads ? CommentExpandOptions.Children : 0;
        const flags: number = CommentExpandOptions.RenderedText + includeChildrenFlag + CommentExpandOptions.Reactions;

        if (!commentPage || lastArtifact !== artifactInfo.artifactId) {
            try {
                this.setState({
                    comments: new ObservableArray<IUICommentListItem>(),
                    commentPage: undefined,
                    parentChildMap: {},
                    isLoading: true,
                    lastError: undefined,
                    lastArtifact: artifactInfo.artifactId
                });
                const firstPage: ICommentPage = await commentsClient.getComments(artifactInfo, 10, false, undefined, undefined, flags);

                if (this.state.lastArtifact === this.props.artifactInfo.artifactId) {
                    commentPage = this._createUICommentPage(firstPage);
                    commentPage.pagedCount = 0;

                    this._updatePage(firstPage, commentPage);
                    this.setState({ isLoading: false });
                    this._usageTelemetry.publishLoadComments(commentPage.totalCount);
                }
            } catch (e) {
                this._handleError("_initialLoad", e);
            }
        }
    }

    /**
     * Adds the specified page of data to the specified comment page
     */
    private _updatePage(page: ICommentPage, pageToUpdate: IUICommentPage): void {
        const { supportsThreads } = this.props;
        let { parentChildMap, commentPage } = this.state;

        pageToUpdate.continuationToken = page.continuationToken;

        if (!commentPage) {
            // initialize the first page
            commentPage = pageToUpdate;
        }

        pageToUpdate.pagedCount += page.comments.length;

        if (supportsThreads) {
            if (page.comments) {
                page.comments.forEach(comment => {
                    if (comment.replies && comment.replies.totalCount > 0) {
                        parentChildMap[comment.id] = this._createUICommentPage(comment.replies, comment.id);
                    } else {
                        parentChildMap[comment.id] = this._createEmptyUICommentPage(comment.id);
                    }
                });
            }
        }

        this.setState({ commentPage, parentChildMap, lastError: undefined });
        this._insertPage(page, pageToUpdate);
    }

    private _insertPage(page: ICommentPage, pageToUpdate: IUICommentPage): void {
        const { supportsThreads, readonly } = this.props;
        const { parentChildMap, comments } = this.state;

        const isRootPage: boolean = !pageToUpdate.parentId;
        let pageInsertIndex: number;
        let isNewPage: boolean = false;

        if (isRootPage) {
            pageInsertIndex = 0;
            if (comments.length === 0) {
                // first insertion into the list, add the 'add' button
                if (!readonly) {
                    comments.splice(0, 0, { type: ItemType.Add, parentId: pageToUpdate.parentId } as IUICommentAdd);
                }
                isNewPage = true;
            } else {
                // if the first element is 'load more' then we need to skip over it or remove it
                let commentItem: IUICommentListItem = comments.value[0];
                if (commentItem.type === ItemType.LoadMore) {
                    if (pageToUpdate.pagedCount === pageToUpdate.totalCount) {
                        // remove the 'load more' if the page is now complete
                        comments.splice(0, 1);
                    } else {
                        pageInsertIndex++;
                    }
                }
            }
        } else {
            // child page, see if it has been initialized by finding the parent and seeing if any
            // children already exist
            pageInsertIndex = this._getCommentIndexById(pageToUpdate.parentId!) + 1;

            if (pageInsertIndex < comments.length) {
                const nextItem: IUICommentListItem = comments.value[pageInsertIndex];
                const nextParentId: number | undefined = this._getParentId(nextItem);

                if (nextParentId === pageToUpdate.parentId) {
                    // we are adding to an existing page.  if the next element is 'load more' then skip it or remove it
                    if (pageToUpdate.pagedCount === pageToUpdate.totalCount) {
                        // remove the 'load more' if the page is now complete
                        comments.splice(pageInsertIndex, 1);
                    } else {
                        pageInsertIndex++;
                    }
                } else {
                    // this is a new page (no children yet), insert the Add
                    if (!readonly) {
                        comments.splice(pageInsertIndex, 0, { type: ItemType.Add, parentId: pageToUpdate.parentId } as IUICommentAdd);
                    }
                    isNewPage = true;
                }
            } else {
                isNewPage = true;
            }
        }

        // add the items to the page.
        if (page.comments) {
            page.comments.forEach(comment => {
                if (comment.isDeleted) {
                    comment.renderedText = Resources.Deleted;
                }

                const reactionTypeToInfoMap: IReactionTypeToInfoMap = {};
                if (comment.reactions) {
                    comment.reactions.forEach((reaction: ICommentReaction) => {
                        const reactionTypeInfo: ICommentReactionTypeInfo = {
                            totalCount: reaction.count,
                            isCurrentUserEngaged: reaction.isCurrentUserEngaged,
                            engagedUsers: [] // will be updated later on when user hovers over this reaction
                        };
                        reactionTypeToInfoMap[reaction.type] = reactionTypeInfo;
                    });
                }

                comments.splice(pageInsertIndex, 0, { type: ItemType.Comment, comment, reactionTypeToInfoMap } as IUIComment);

                if (supportsThreads && isRootPage) {
                    if (comment.replies && comment.replies.totalCount > 0) {
                        // recurse to insert this page as well.
                        this._insertPage(comment.replies, parentChildMap[comment.id]);
                    } else {
                        // add the 'add/reply' component
                        if (!readonly) {
                            comments.splice(pageInsertIndex + 1, 0, { type: ItemType.Add, parentId: comment.id } as IUICommentAdd);
                        }
                    }
                }
            });
        }

        if (isNewPage && pageToUpdate.pagedCount < pageToUpdate.totalCount) {
            comments.splice(pageInsertIndex, 0, { type: ItemType.LoadMore, parentId: pageToUpdate.parentId } as IUICommentLoadMore);
        }
    }

    private _createUICommentPage(page: ICommentPage, parentId?: number): IUICommentPage {
        return {
            totalCount: page.totalCount,
            continuationToken: page.continuationToken,
            parentId: parentId,
            pagedCount: page.comments.length
        };
    }

    private _createEmptyUICommentPage(parentId?: number): IUICommentPage {
        return { totalCount: 0, pagedCount: 0, continuationToken: "", parentId };
    }

    private _getPageStartIndex(parentId?: number): number {
        const { comments } = this.state;

        if (comments.length === 0 || !parentId) {
            return 0;
        }

        const parentIndex: number = this._getCommentIndexById(parentId);

        if (parentIndex < 0 || parentIndex >= comments.length) {
            return -1;
        }

        return parentIndex + 1;
    }

    private _getPageEndIndex(parentId?: number): number {
        const { comments } = this.state;

        const startIndex: number = this._getPageStartIndex(parentId);

        if (parentId) {
            if (startIndex >= 0) {
                let index: number = startIndex;

                while (index < comments.length) {
                    const commentItem: IUICommentListItem = comments.value[index];
                    const itemParentId: number | undefined = this._getParentId(commentItem);

                    if (itemParentId !== parentId) {
                        return index - 1;
                    }

                    index++;
                }

                return index - 1;
            }
        } else {
            // root comment thread, return the index of the last item.
            return comments.length - 1;
        }

        return -1;
    }

    private _getParentId(commentItem: IUICommentListItem): number | undefined {
        switch (commentItem.type) {
            case ItemType.Add:
                const addItem: IUICommentAdd = commentItem;
                return addItem.parentId;
            case ItemType.LoadMore:
                const loadMoreItem: IUICommentLoadMore = commentItem;
                return loadMoreItem.parentId;
            case ItemType.Comment:
                const uiComment: IUIComment = commentItem as IUIComment;
                return uiComment.comment.parentId;
        }

        return -1;
    }

    private _getCommentIndex(item: IUICommentListItem): number {
        const { comments } = this.state;

        return comments.value.findIndex((val: IUICommentListItem) => val === item);
    }

    private _getCommentIndexById(commentId: number): number {
        const { comments } = this.state;

        return comments.value.findIndex((uiComment: IUICommentListItem) => {
            if (uiComment.type === ItemType.Comment) {
                return (uiComment as IUIComment).comment.id === commentId;
            }
            return false;
        });
    }

    private _handleError(method: string, e: any, item?: IUICommentListItem): void {
        if (e && !e.canceled) {
            this._usageTelemetry.traceError("CommentThread." + method, e);

            if (item) {
                this._updateItemError(item, e.message);
            } else {
                this.setState({ lastError: e.message });
            }
        }
    }

    private _getCurrentUser(): IdentityRef | undefined {
        const pageService: IVssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();
        return pageData ? pageData.user : undefined;
    }

    private _isCurrentUserCommentCreator(comment: IComment): boolean {
        return (
            this._currentUser != null &&
            ((comment.createdOnBehalfOf && this._currentUser.id === comment.createdOnBehalfOf.id) ||
                this._currentUser.id === comment.createdBy.id)
        );
    }

    /**
     * Optional callback for user clicking a reaction type to add a new reaction.
     * @param commentId Comment ID
     * @param reactionType Type of reaction
     * @param isAddedFromStatusBar Flag to indicate if reaction was added from statusbar or selector
     */
    private _onAddReaction = async (commentId: number, reactionType: CommentReactionType, isAddedFromStatusBar: boolean): Promise<void> => {
        const { commentsClient, artifactInfo } = this.props;
        const addedReaction: ICommentReaction = await commentsClient.addCommentReaction(artifactInfo, commentId, reactionType);
        const { comments } = this.state;
        const commentIndex: number = this._getCommentIndexById(commentId);
        const uiComment: IUIComment = comments.value[commentIndex] as IUIComment;

        uiComment.reactionTypeToInfoMap = this._getUpdatedReactionTypeInfoMap(uiComment.reactionTypeToInfoMap, addedReaction);
        comments.change(commentIndex, uiComment);
    };

    /**
     * Optional callback for user clicking a reaction type to remove an existing reaction.
     * @param commentId Comment ID
     * @param reactionType Type of reaction
     * @param isDeletedFromStatusBar Flag to indicate if reaction was deleted from statusbar or selector
     */
    private _onDeleteReaction = async (
        commentId: number,
        reactionType: CommentReactionType,
        isDeletedFromStatusBar: boolean
    ): Promise<void> => {
        const { commentsClient, artifactInfo } = this.props;
        const deletedReaction: ICommentReaction = await commentsClient.deleteCommentReaction(artifactInfo, commentId, reactionType);
        const { comments } = this.state;
        const commentIndex: number = this._getCommentIndexById(commentId);
        const uiComment: IUIComment = comments.value[commentIndex] as IUIComment;

        uiComment.reactionTypeToInfoMap = this._getUpdatedReactionTypeInfoMap(uiComment.reactionTypeToInfoMap, deletedReaction);
        comments.change(commentIndex, uiComment);
    };

    /**
     * Optional callback to get the users engaged on a certain reaction type.
     * @param commentId Comment ID
     * @param reactionType Type of reaction
     * @param top Maximum number of users to fetch
     */
    private _onGetEngagedUsers = async (commentId: number, reactionType: CommentReactionType, top: number): Promise<void> => {
        const { commentsClient, artifactInfo } = this.props;
        const identities: IdentityRef[] = await commentsClient.getReactionRecentUsers(artifactInfo, commentId, reactionType);
        const commentIndex: number = this._getCommentIndexById(commentId);

        const { comments } = this.state;
        const uiComment: IUIComment = comments.value[commentIndex] as IUIComment;
        if (identities && uiComment.reactionTypeToInfoMap && uiComment.reactionTypeToInfoMap[reactionType]) {
            const engagedUsers = identities.map(identity => {
                return {
                    identityId: identity.id,
                    identityDisplayName: identity.displayName
                };
            });
            uiComment.reactionTypeToInfoMap[reactionType].engagedUsers = engagedUsers;
            uiComment.reactionTypeToInfoMap[reactionType].totalCount = engagedUsers.length;

            comments.change(commentIndex, uiComment);
        }
    };

    private _getCommentReactions = (commentId: number): IReactionTypeToInfoMap => {
        const { comments } = this.state;
        const commentIndex: number = this._getCommentIndexById(commentId);
        const uiComment: IUIComment = comments.value[commentIndex] as IUIComment;

        return uiComment.reactionTypeToInfoMap || {};
    };

    private _getUpdatedReactionTypeInfoMap(
        originalReactionTypeToInfoMap: IReactionTypeToInfoMap | undefined,
        reaction: ICommentReaction
    ): IReactionTypeToInfoMap {
        const reactionTypeInfo: ICommentReactionTypeInfo = {
            totalCount: reaction.count,
            isCurrentUserEngaged: reaction.isCurrentUserEngaged,
            engagedUsers: [] // will be updated later on when user hovers over this reaction
        };

        const reactionTypeToInfoMap = { ...originalReactionTypeToInfoMap } as IReactionTypeToInfoMap;
        reactionTypeToInfoMap[reaction.type] = reactionTypeInfo;

        return reactionTypeToInfoMap;
    }

    private _updateDirtyCount = (isDirty: boolean): void => {
        const { onDirtyChange } = this.props;
        const oldDirtyState: boolean = this._isDirtyCount > 0;
        isDirty ? this._isDirtyCount++ : this._isDirtyCount--;
        const newDirtyState: boolean = this._isDirtyCount > 0;
        oldDirtyState != newDirtyState && onDirtyChange && onDirtyChange(newDirtyState);
    };
}

VssComponent.register("comment-thread", CommentThread);
